import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Select, Spin } from "antd";
import { RollbackOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { apiClient } from "../../../shared/api/AllApi";
import { parseLocalizedName } from "../../../utils/localization";
import { formatCurrency } from "../../../utils/formatters";
import { readCachedSystemConfig } from "../../../shared/lib/systemConfig";
import { formatRangeParams, resolveDatePreset } from "../../../shared/lib/dateFilterPresets";
import { orderDocNo } from "../../../shared/lib/documentNumbers";
import { useTranslation } from "react-i18next";
import DateRangeFilter from "../../../shared/ui/DateRangeFilter";

const card = {
  background: "var(--pos-surface)",
  border: "1px solid var(--pos-border)",
  borderRadius: 14,
  padding: "16px 20px",
};

const PosSubReturns = ({ onBack, vendors = [] }) => {
  const { t, i18n } = useTranslation();
  const currency = readCachedSystemConfig()?.currency_code || "IQD";

  const [dateRange, setDateRange] = useState(() => resolveDatePreset("today", { withTime: true }));
  const [vendorId, setVendorId] = useState(null);
  const [reason, setReason] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        ...formatRangeParams(dateRange, { withTime: true, fromKey: "date_from", toKey: "date_to" }),
      };
      if (vendorId) params.vendor_id = vendorId;
      if (reason) params.reason = reason;
      const r = await apiClient.get("/pos/returns", { params });
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [dateRange, vendorId, reason]);

  useEffect(() => { load(); }, [load]);

  const reasonOptions = useMemo(() => {
    const set = new Set((data?.by_reason || []).map((r) => r.reason).filter(Boolean));
    return Array.from(set).map((v) => ({ value: v, label: v }));
  }, [data]);

  const vendorOptions = useMemo(
    () => vendors.map((v) => ({ value: v.id, label: v.name || `Vendor ${v.id}` })),
    [vendors]
  );

  const productName = (p, id) => parseLocalizedName(p, i18n.language, `#${id}`);

  const stat = (label, value, color) => (
    <div style={{ ...card, flex: 1, minWidth: 160, textAlign: "center" }}>
      <div style={{ fontSize: 24, fontWeight: 900, color }}>{value}</div>
      <div style={{ fontSize: 12, color: "var(--pos-text-faint)", marginTop: 4, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
    </div>
  );

  const returns = data?.returns || [];

  return (
    <div className="pos-sub-view">
      <div className="pos-sub-header">
        <div className="pos-sub-header-left">
          <div className="pos-sub-icon"><RollbackOutlined /></div>
          <h2 className="pos-sub-title">{t("posReturnsTitle", "Returns History")}</h2>
          <span className="pos-sub-tab-badge" style={{ fontSize: 12, padding: "2px 10px" }}>{data?.total_returns ?? 0}</span>
        </div>
        <button type="button" className="pos-sub-back" onClick={onBack}>{t("posBackToPOS", "Back to POS")}</button>
      </div>

      <div className="pos-sub-list">
        <Spin spinning={loading}>
          {/* Stats */}
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
            {stat(t("posReturnsTotal", "Total Returns"), data?.total_returns ?? 0, "#ef4444")}
            {stat(t("posReturnsItems", "Items Returned"), data?.items_returned ?? 0, "#f59e0b")}
            {stat(t("posOrdersCashBack", "Total Cash Back"), formatCurrency(data?.total_refund || 0, currency), "#16a34a")}
            {stat(t("posReturnsAvg", "Avg Return Value"), formatCurrency(data?.avg_refund || 0, currency), "#16a34a")}
          </div>

          {/* Filters */}
          <div style={{ ...card, marginBottom: 16, display: "flex", gap: 14, alignItems: "flex-end", flexWrap: "wrap" }}>
            <DateRangeFilter
              value={dateRange}
              onChange={(v) => setDateRange(v && v[0] && v[1] ? v : resolveDatePreset("today", { withTime: true }))}
              showTime
              allowClear={false}
              size="small"
            />
            <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 12, color: "var(--pos-text-mute)" }}>
              {t("posPurchaseVendor", "Farmer")}
              <Select
                allowClear
                placeholder={t("allFarmers", "All Farmers")}
                value={vendorId}
                onChange={setVendorId}
                options={vendorOptions}
                style={{ width: 200 }}
                showSearch
                optionFilterProp="label"
                getPopupContainer={() => document.body}
                classNames={{ popup: { root: "pos-dropdown-popup" } }}
              />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 12, color: "var(--pos-text-mute)" }}>
              {t("reason", "Reason")}
              <Select
                allowClear
                placeholder={t("allReasons", "All Reasons")}
                value={reason}
                onChange={setReason}
                options={reasonOptions}
                style={{ width: 200 }}
                getPopupContainer={() => document.body}
                classNames={{ popup: { root: "pos-dropdown-popup" } }}
              />
            </label>
            <button
              type="button"
              className="pos-sub-add-btn"
              style={{ height: 40 }}
              onClick={load}
            >
              {t("filter", "Filter")}
            </button>
          </div>

          {/* Return details */}
          <div style={{ ...card, padding: 0, marginBottom: 16, overflow: "hidden" }}>
            <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--pos-border)", fontWeight: 700, fontSize: 15, color: "var(--pos-text)" }}>
              {t("posReturnsDetails", "Return Details")}
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "var(--pos-surface-2)", color: "var(--pos-text-faint)" }}>
                    {[t("date", "Date"), t("posReturnsSaleId", "Sale #"), t("customer", "Customer"), t("product", "Product"),
                      t("posPurchaseVendor", "Farmer"), t("quantity", "Qty"), t("weight", "Weight"), t("price", "Unit Price"),
                      t("posReturnsRefund", "Total Refund"), t("reason", "Reason"),
                      t("returnedBy", "Returned by"), t("soldBy", "Sold by")].map((h, i) => (
                      <th key={i} style={{ textAlign: i > 4 && i < 9 ? "right" : "left", padding: "10px 14px", fontSize: 11, fontWeight: 800, textTransform: "uppercase", whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {returns.length === 0 ? (
                    <tr><td colSpan={12} style={{ textAlign: "center", padding: 40, color: "var(--pos-text-faint)" }}>{t("noDataFound", "No returns")}</td></tr>
                  ) : (
                    returns.map((r) => (
                      <tr key={r.id} style={{ borderTop: "1px solid var(--pos-border-soft)", color: "var(--pos-text)" }}>
                        <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>{dayjs(r.created_at).format("MMM D, YYYY HH:mm")}</td>
                        <td style={{ padding: "10px 14px", fontWeight: 700, color: "var(--pos-accent)", fontFamily: "monospace", fontSize: 12 }}>{orderDocNo(r)}</td>
                        <td style={{ padding: "10px 14px", fontWeight: 600 }}>{r.customer || t("walkIn", "Walk-in")}</td>
                        <td style={{ padding: "10px 14px", fontWeight: 600 }}>{productName(r.product, r.product_id)}</td>
                        <td style={{ padding: "10px 14px", color: "var(--pos-text-mute)" }}>{r.vendor || "—"}</td>
                        <td style={{ padding: "10px 14px", textAlign: "right" }}>{Number(r.return_qty) > 0 ? r.return_qty : "—"}</td>
                        <td style={{ padding: "10px 14px", textAlign: "right" }}>{Number(r.return_weight) > 0 ? `${r.return_weight} ${t("kg", "KG")}` : "—"}</td>
                        <td style={{ padding: "10px 14px", textAlign: "right" }}>{formatCurrency(r.unit_price, currency)}</td>
                        <td style={{ padding: "10px 14px", textAlign: "right", fontWeight: 700, color: "#16a34a" }}>
                          {formatCurrency(r.refund_amount, currency)}
                          {Number(r.commission_refund_amount) > 0 && (
                            <div style={{ fontSize: 11, fontWeight: 500, color: "var(--pos-text-faint)" }}>
                              {t("inclCommission", "incl. commission")} {formatCurrency(r.commission_refund_amount, currency)}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: "10px 14px", color: "var(--pos-text-mute)" }}>{r.reason || "—"}</td>
                        <td style={{ padding: "10px 14px", color: "var(--pos-text-mute)" }}>{r.returned_by || r.user || "—"}</td>
                        <td style={{ padding: "10px 14px", color: "var(--pos-text-mute)" }}>{r.sold_by || "—"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Breakdowns */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div style={{ ...card, padding: 0, overflow: "hidden" }}>
              <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--pos-border)", fontWeight: 700, color: "var(--pos-text)" }}>
                {t("posReturnsByReason", "Returns by Reason")}
              </div>
              {(data?.by_reason || []).length === 0 ? (
                <div style={{ padding: 20, color: "var(--pos-text-faint)", fontSize: 13 }}>—</div>
              ) : (data.by_reason).map((r, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "12px 20px", borderTop: i ? "1px solid var(--pos-border-soft)" : "none", color: "var(--pos-text)" }}>
                  <span>{r.reason}</span>
                  <span style={{ color: "var(--pos-text-mute)" }}>{r.count} {t("items", "items")} ({formatCurrency(r.amount, currency)})</span>
                </div>
              ))}
            </div>
            <div style={{ ...card, padding: 0, overflow: "hidden" }}>
              <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--pos-border)", fontWeight: 700, color: "var(--pos-text)" }}>
                {t("posReturnsByFarmer", "Returns by Farmer")}
              </div>
              {(data?.by_farmer || []).length === 0 ? (
                <div style={{ padding: 20, color: "var(--pos-text-faint)", fontSize: 13 }}>—</div>
              ) : (data.by_farmer).map((r, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "12px 20px", borderTop: i ? "1px solid var(--pos-border-soft)" : "none", color: "var(--pos-text)" }}>
                  <span>{r.vendor}</span>
                  <span style={{ color: "var(--pos-text-mute)" }}>{r.count} {t("items", "items")} ({formatCurrency(r.amount, currency)})</span>
                </div>
              ))}
            </div>
          </div>
        </Spin>
      </div>
    </div>
  );
};

export default PosSubReturns;
